<script setup lang="ts">
  import { computed, onUnmounted, ref, watch } from 'vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { CasesService } from '@/types/generated'
  import type { TCase } from '@/types/generated/types.gen'
  import { ChevronLeft, Eye, Folder } from 'lucide-vue-next'

  interface CaseResult {
    uuid: string
    label: string
  }

  const props = withDefaults(
    defineProps<{
      searchQuery?: string
    }>(),
    {
      searchQuery: ''
    }
  )

  const emit = defineEmits<{
    (e: 'select', uuid: string, label: string): void
    (e: 'preview', uuid: string, label: string): void
    (e: 'back'): void
    (e: 'update:searchQuery', value: string): void
  }>()

  const query = computed({
    get: () => props.searchQuery,
    set: (value: string) => emit('update:searchQuery', value)
  })
  const results = ref<CaseResult[]>([])
  const loading = ref(false)
  const searchInputRef = ref<HTMLInputElement | null>(null)

  let debounceTimer: ReturnType<typeof setTimeout> | null = null
  let fetchGeneration = 0

  onUnmounted(() => {
    if (debounceTimer) clearTimeout(debounceTimer)
  })

  function getCaseLabel(c: TCase): string {
    if ('case_title' in c && c.case_title) return c.case_title
    if ('jupus_case_number' in c && c.jupus_case_number) return c.jupus_case_number
    return c.uuid ?? ''
  }

  async function fetchCases(search: string) {
    const gen = ++fetchGeneration
    loading.value = true
    try {
      const response = await CasesService.casesList({ search, limit: 20 })
      if (gen !== fetchGeneration) return
      results.value = response.results.map((c) => ({
        uuid: c.uuid ?? '',
        label: getCaseLabel(c)
      }))
    } catch {
      if (gen !== fetchGeneration) return
      results.value = []
    } finally {
      if (gen === fetchGeneration) loading.value = false
    }
  }

  watch(
    query,
    (val) => {
      if (debounceTimer) clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => {
        fetchCases(val.trim())
      }, 300)
    },
    { immediate: true }
  )

  function onSelect(c: CaseResult) {
    emit('select', c.uuid, c.label)
  }

  // Auto-focus the search input when the panel mounts
  watch(searchInputRef, (el) => {
    if (el) el.focus()
  })

  const { t } = useTranslation({
    en: { search: 'Search', cases: 'Cases', back: 'Back', preview: 'Preview' },
    de: { search: 'Suchen', cases: 'Akten', back: 'Zurück', preview: 'Vorschau' },
    fr: { search: 'Rechercher', cases: 'Dossiers', back: 'Retour', preview: 'Aperçu' }
  })
</script>

<template>
  <div class="flex h-72 w-72 flex-col overflow-hidden">
    <!-- Search header -->
    <div class="flex items-start bg-white py-2.5 pr-4">
      <button
        type="button"
        :aria-label="t('back')"
        class="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-white"
        @click="emit('back')"
      >
        <ChevronLeft
          class="h-6 w-6 text-gray-800"
          :stroke-width="1.5"
        />
      </button>
      <div class="flex h-9 flex-1 items-center rounded-[30px] bg-gray-50 px-3 py-2.5">
        <input
          ref="searchInputRef"
          v-model="query"
          type="text"
          class="w-full border-none bg-transparent p-0 text-sm font-normal leading-5 text-gray-800 font-inter
            placeholder:text-gray-500 focus:outline-none focus:ring-0"
          :placeholder="t('search')"
        />
      </div>
    </div>

    <!-- Results -->
    <div class="relative flex-1 overflow-y-auto">
      <div class="pt-3">
        <div class="px-4">
          <div class="text-xs font-normal leading-4 text-gray-500">
            {{ t('cases') }}
          </div>
        </div>

        <div
          v-if="loading && results.length === 0"
          class="flex items-center justify-center py-8"
        >
          <svg
            class="h-5 w-5 animate-spin text-gray-400"
            viewBox="0 0 24 24"
            fill="none"
          >
            <circle
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="2"
              opacity="0.25"
            />
            <path
              d="M22 12a10 10 0 0 0-10-10"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
        </div>

        <template v-else>
          <div
            v-for="c in results"
            :key="c.uuid"
            class="flex w-full items-center gap-2.5 border-b border-gray-50 p-4 hover:bg-gray-50"
          >
            <Folder
              class="h-5 w-5 shrink-0 text-gray-800"
              :stroke-width="1.25"
            />
            <button
              type="button"
              class="line-clamp-1 flex-1 text-left text-sm font-normal leading-5 text-gray-800 font-inter"
              @click="onSelect(c)"
            >
              {{ c.label }}
            </button>
            <button
              type="button"
              :aria-label="t('preview')"
              class="flex shrink-0 items-center justify-center rounded-sm hover:bg-gray-100"
              @click.stop="emit('preview', c.uuid, c.label)"
            >
              <Eye
                class="h-5 w-5 text-gray-800"
                :stroke-width="1.5"
              />
            </button>
          </div>
        </template>
      </div>

      <!-- Bottom fade gradient -->
      <div
        class="pointer-events-none sticky bottom-0 left-0 h-14 w-full bg-gradient-to-b from-white/0 to-white"
      />
    </div>
  </div>
</template>
